import { AppInitLoader } from "@/components/loader/app-init-loader";
import { SubmissionDetail } from "@/components/pure/submission-detail/submissions-detail";
import { LapsedSubscriptionModal } from "@/components/subscription/LapsedSubscriptionModal";
import { PlusSubscriptionRequired } from "@/components/subscription/PlusSubscriptionRequired";
import { ExpiredTrialModal } from "@/components/trial/ExpiredTrialModal";
import { DB_ORG } from "@/constants/db";
import { useLogout } from "@/hooks/auth-queries";
import { useSubscriptionStatusCheck } from "@/hooks/use-subscription-status-check";
import { useTrialExpirationCheck } from "@/hooks/use-trial-expiration-check";
import { CommandCenter } from "@/layouts/command-center/CommandCenter";
import { Copyright } from "@/layouts/signed-in/Copyright/Copyright";
import { Navbar } from "@/layouts/signed-in/Navbar/Navbar";
import { Sidebar } from "@/layouts/signed-in/Sidebar/Sidebar";
import { SubscriptionTab } from "@/layouts/signed-in/views/Admin/SubscriptionTab/SubscriptionTab";
import { ReportsTab } from "@/layouts/signed-in/views/Admin/ReportsTab/ReportsTab";
import { AssetsTable } from "@/layouts/signed-in/views/Admin/AssetsTable/children/AssetsTable";
import { UserAccessLevel } from "@/stores/user-store";
import { useUIModeStore } from "@/stores/ui-mode-store";
import { LocationsTable } from "@/layouts/signed-in/views/Locations/LocationsTable";
import { selfUpdateUserComprehensive } from "@/services/user-service";
import { usePageStore, useUserStore } from "@/stores";
import { Admin } from "@/views/SignedIn/Admin/Admin";
import { AssetScanner } from "@/views/SignedIn/AssetScanner/AssetScanner";
import { BudgetSimulationPage } from "@/views/SignedIn/BudgetSimulation/BudgetSimulationPage";
import { CWDashboard } from "@/views/SignedIn/CWDashboard/CWDashboard";
import { Dashboard } from "@/views/SignedIn/Dashboard/Dashboard";
import { FormHandler } from "@/views/SignedIn/Forms/FormHandler";
import { Forms } from "@/views/SignedIn/Forms/Forms";
import LocationSettings from "@/views/SignedIn/LocationSettings/LocationSettings";
import { Plans } from "@/views/SignedIn/Plans/Plans";
import { PredSpendTab } from "@/views/SignedIn/PredictiveSpend/PredSpendTab";
import { Profile } from "@/views/SignedIn/Profile/Profile";
import { Releases } from "@/views/SignedIn/Releases/Releases";
import { SingleLocation } from "@/views/SignedIn/SingleLocation/SingleLocation";
import { SlimFormCapture } from "@/views/SignedIn/SlimForm/SlimFormCapture";
import { SlimFormQueue } from "@/views/SignedIn/SlimForm/SlimFormQueue";
import { SlimFormReview } from "@/views/SignedIn/SlimForm/SlimFormReview";
import { WorkOrders } from "@/views/SignedIn/WorkOrders/WorkOrders";
import { WorkOrderDetailPage } from "@/views/SignedIn/WorkOrders/WorkOrderDetailPage";
import { WorkOrderVendors } from "@/views/SignedIn/WorkOrders/WorkOrderVendors";
import { WorkOrderRules } from "@/views/SignedIn/WorkOrders/WorkOrderRules";
import { WorkOrderCreatePage } from "@/views/SignedIn/WorkOrders/WorkOrderCreatePage";
import { Inventory } from "@/views/SignedIn/Inventory/Inventory";
import { InventoryItemDetail } from "@/views/SignedIn/Inventory/InventoryItemDetail";
import DataCollectionRecords from "@/views/SignedIn/DataCollection/DataCollectionRecords";
import CollectionRecordDetail from "@/views/SignedIn/DataCollection/CollectionRecordDetail";
import FieldDataCapture from "@/views/SignedIn/DataCollection/FieldDataCapture";
import FacilityLens from "@/views/SignedIn/FacilityLens/FacilityLens";
import { FacilityLink } from "@/views/SignedIn/FacilityLink/FacilityLink";
import { FacilitySolveTriage } from "@/views/SignedIn/FacilitySolve/FacilitySolve";
import { FacilitySolveSession } from "@/views/SignedIn/FacilitySolve/FacilitySolveSession";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { message } from "antd";
import { useEffect, useState } from "react";
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";
import { Col, Container, Row } from "reactstrap";

const SignedIn = () => {
	const { user, setUser } = useUserStore();
	const { setIsLoading } = usePageStore();
	const { mode } = useUIModeStore();
	const hasWorkOrders = useFeatureFlag("work_orders");
	const hasDataCollection = useFeatureFlag("data_collection");
	const hasFacilityLink = useFeatureFlag("facility_link");
	const hasInventory = useFeatureFlag("inventory");
	
	const [dashboard, setDashboard] = useState<any>();
	const { showExpiredModal, closeExpiredModal } = useTrialExpirationCheck();
	const { showLapsedModal, closeLapsedModal, subscriptionStatus } =
		useSubscriptionStatusCheck();
	const location = useLocation();
	const navigate = useNavigate();

	const logout = useLogout();

	const handleLogout = async () => {
		try {
			const result = await logout();
			if (result.success) {
				message.success("Logged out successfully");
			} else {
				message.error(result.error || "Failed to logout");
			}
		} catch (error) {
			console.error("An error occurred during logout", error);
			message.error("An error occurred during logout");
		}
	};

	const updateUserOrg = async (orgId: string, _orgName: string) => {
		if (!user || user.organization?.external_firebase_id === orgId) return;

		const previousUser = user;

		// Check both snake_case and camelCase versions of allowed_organizations
		const allowedOrgs =
			user.allowed_organizations || user.allowedOrganizations || [];

		if (allowedOrgs.length === 0) {
			console.error("No allowed organizations found for user:", user);
			message.error("Unable to switch organizations. Please refresh the page.");
			return;
		}

		const selectedOrg = allowedOrgs.find(
			(org) =>
				org.external_firebase_id === orgId || org.externalFirebaseId === orgId,
		);

		if (!selectedOrg) {
			console.error("Selected organization not found:", { orgId, allowedOrgs });
			message.error("Selected organization not found.");
			return;
		}

		const oldOrgId = user.organization?.id || user.organization_id;

		try {
			setIsLoading(true);

			// STEP 1: Reset all organization-specific state BEFORE switching
			const { resetOrganizationState } = await import(
				"@/utils/org-switch-manager"
			);
			await resetOrganizationState(oldOrgId, selectedOrg.id);

			// STEP 2: Sync with backend and get complete user data
			const response = await selfUpdateUserComprehensive({
				...user,
				organization_id: selectedOrg.id,
			});

			// STEP 3: Update user in store
			setUser(response);

			message.success("Organization has been changed");

			// STEP 4: Navigate to home to force component remounts
			navigate("/", { replace: true });

			// STEP 5: Optional - force page reload for guaranteed clean slate
			// Uncomment if you still see stale data issues:
			// setTimeout(() => window.location.reload(), 100);
		} catch (error: any) {
			console.error("Failed to switch organization:", error);
			// Rollback on failure
			setUser(previousUser);
			message.error(error.message || "Failed to update organization");
		} finally {
			setIsLoading(false);
		}
	};

	// Helper function to check if trial is expired
	const isTrialExpired = (): boolean => {
		if (!user?.is_trial && !user?.isTrial && !user?.isTrialAccount) {
			return false;
		}

		const trialExpirationDate =
			user?.trialExpirationDate || user?.trial_expiration_date;
		if (!trialExpirationDate) {
			// If no expiration date, check if account is disabled as fallback
			return user?.isAccountDisabled || false;
		}

		const expirationDate = new Date(trialExpirationDate);
		const now = new Date();
		return expirationDate <= now;
	};

	useEffect(() => {
		const getData = async () => {
			if (!user || !user.organization) return;
			if (user.organization.external_firebase_id === DB_ORG.CUSHMANWAKEFIELD) {
				setDashboard(<CWDashboard />);
			} else {
				setDashboard(<Dashboard />);
			}
		};
		getData();
	}, [user]);

	// Redirect logic is handled by TrialRestrictedRoute component below
	// Removed useEffect to prevent infinite redirect loops

	// Component to handle expired trial user restrictions
	const TrialRestrictedRoute = ({
		children,
	}: { children: React.ReactNode }) => {
		// Only redirect expired trial users to subscription page
		// Allow access to subscription page and admin/subscription route
		const isExpired = isTrialExpired();
		const isSubscriptionRoute = location.pathname === "/subscription";
		const isAdminSubscriptionRoute =
			location.pathname === "/admin/subscription";

		if (isExpired && !isSubscriptionRoute && !isAdminSubscriptionRoute) {
			// If trying to access admin but not subscription tab, redirect to subscription tab
			if (location.pathname.startsWith("/admin")) {
				return <Navigate to="/admin/subscription" replace />;
			}
			// Otherwise redirect to subscription page
			return <Navigate to="/subscription" replace />;
		}
		return <>{children}</>;
	};

	// ─── Command Center Mode ───
	// When in command-center mode AND on the root path, show the AI-first interface.
	// Only render if user is fully loaded (has organization data).
	// If the user navigates to a specific route (e.g., from a chat action), they'll
	// automatically be in deep-data mode (the CommandCenter switches mode before navigating).
	if (mode === "command-center" && location.pathname === "/" && user?.organization) {
		return (
			<>
				<CommandCenter />
				{/* Modals still need to work */}
				<ExpiredTrialModal open={showExpiredModal} onClose={closeExpiredModal} />
				<LapsedSubscriptionModal
					open={showLapsedModal}
					onClose={closeLapsedModal}
					subscriptionStatus={subscriptionStatus?.status as any}
				/>
			</>
		);
	}

	// ─── Deep Data Mode (Traditional UI) ───
	return (
		<div className="page m-0 vw-100">
			<Row className="m-0 vw-100">
				<Container fluid className="p-0 m-0 vw-100">
					<div className="d-flex flex-column flex-md-row">
						<Sidebar
							isSidebarOpen={false}
							user={user!}
							updateUserOrg={updateUserOrg}
							logout={handleLogout}
						/>
						<Col className="px-2 pt-1 main-page-container min-vh-100">
							<Navbar />
							<Routes>
								<Route
									path="/"
									element={
										<TrialRestrictedRoute>
											{dashboard || <AppInitLoader />}
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="profile"
									element={
										<TrialRestrictedRoute>
											<Profile />
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="admin/*"
									element={
										<TrialRestrictedRoute>
											<Admin />
										</TrialRestrictedRoute>
									}
								/>
								{/* Subscription page - always accessible */}
								<Route path="subscription" element={<SubscriptionTab />} />
								<Route
									path="plans"
									element={
										<TrialRestrictedRoute>
											<Plans />
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="locations/*"
									element={
										<TrialRestrictedRoute>
											<LocationsTable />
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="locations/:id/*"
									element={
										<TrialRestrictedRoute>
											<SingleLocation />
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="locations/:id/settings"
									element={
										<TrialRestrictedRoute>
											<LocationSettings />
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="locations/:id/submissions/:submissionId/"
									element={
										<TrialRestrictedRoute>
											<SubmissionDetail />
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="forms"
									element={
										<TrialRestrictedRoute>
											<Forms />
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="forms/:id/"
									element={
										<TrialRestrictedRoute>
											<FormHandler user={user} />
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="asset-scanner"
									element={
										<TrialRestrictedRoute>
											{user?.hasPlusSubscription ||
											user?.is_trial || user?.isTrial ||
											user?.isTrialAccount ? (
												<AssetScanner />
											) : (
												<PlusSubscriptionRequired featureName="Asset Scanner" />
											)}
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="releases"
									element={
										<TrialRestrictedRoute>
											<Releases />
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="predictive-spend"
									element={
										<TrialRestrictedRoute>
											{user?.hasPlusSubscription ||
											user?.is_trial || user?.isTrial ||
											user?.isTrialAccount ? (
												<PredSpendTab />
											) : (
												<PlusSubscriptionRequired featureName="Predictive Spend" />
											)}
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="budget-simulation"
									element={
										<TrialRestrictedRoute>
											{user?.hasPlusSubscription ||
											user?.is_trial || user?.isTrial ||
											user?.isTrialAccount ? (
												<BudgetSimulationPage />
											) : (
												<PlusSubscriptionRequired featureName="Budget Simulation" />
											)}
										</TrialRestrictedRoute>
									}
								/>
								{/* Assets - moved from Admin to sidebar */}
								<Route
									path="assets"
									element={
										<TrialRestrictedRoute>
											<AssetsTable />
										</TrialRestrictedRoute>
									}
								/>
								{/* Quality Check - moved from Admin to sidebar */}
								<Route
									path="quality-check"
									element={
										<TrialRestrictedRoute>
											{user?.hasPlusSubscription ||
											user?.is_trial || user?.isTrial ||
											user?.isTrialAccount ? (
												<SlimFormQueue />
											) : (
												<PlusSubscriptionRequired featureName="Quality Check" />
											)}
										</TrialRestrictedRoute>
									}
								/>
								{/* Reports - moved from Admin to sidebar (Super Admin only) */}
								<Route
									path="reports"
									element={
										<TrialRestrictedRoute>
											{(user?.role?.access_level ?? user?.role?.accessLevel ?? 0) >=
											UserAccessLevel.SUPER_ADMIN ? (
												<ReportsTab organization={user?.organization} />
											) : (
												<Navigate to="/" replace />
											)}
										</TrialRestrictedRoute>
									}
								/>
							{/* Inventory (feature flag gated) */}
							{hasInventory && (
								<>
									<Route
										path="inventory"
										element={
											<TrialRestrictedRoute>
												<Inventory />
											</TrialRestrictedRoute>
										}
									/>
									<Route
										path="inventory/items/:id"
										element={
											<TrialRestrictedRoute>
												<InventoryItemDetail />
											</TrialRestrictedRoute>
										}
									/>
								</>
							)}
							{/* Work Orders (feature flag gated) */}
							{hasWorkOrders && (
								<>
									<Route
										path="work-orders"
										element={
											<TrialRestrictedRoute>
												<WorkOrders />
											</TrialRestrictedRoute>
										}
									/>
									<Route
										path="work-orders/new"
										element={
											<TrialRestrictedRoute>
												<WorkOrderCreatePage />
											</TrialRestrictedRoute>
										}
									/>
									<Route
										path="work-orders/:id"
										element={
											<TrialRestrictedRoute>
												<WorkOrderDetailPage />
											</TrialRestrictedRoute>
										}
									/>
									<Route
										path="work-orders/vendors"
										element={
											<TrialRestrictedRoute>
												<WorkOrderVendors />
											</TrialRestrictedRoute>
										}
									/>
									<Route
										path="work-orders/rules"
										element={
											<TrialRestrictedRoute>
												<WorkOrderRules />
											</TrialRestrictedRoute>
										}
									/>
									<Route
										path="work-orders/triage"
										element={
											<TrialRestrictedRoute>
												<FacilitySolveTriage />
											</TrialRestrictedRoute>
										}
									/>
									<Route
										path="work-orders/triage/:id"
										element={
											<TrialRestrictedRoute>
												<FacilitySolveSession />
											</TrialRestrictedRoute>
										}
									/>
								</>
							)}
						{/* FacilityLink (feature flag gated) */}
						{hasFacilityLink && (
							<Route
								path="facility-link"
								element={
									<TrialRestrictedRoute>
										<FacilityLink />
									</TrialRestrictedRoute>
								}
							/>
						)}
							{/* Data Collection (feature flag gated) */}
							{hasDataCollection && (
								<>
									<Route
										path="data-collection"
										element={
											<TrialRestrictedRoute>
												<DataCollectionRecords />
											</TrialRestrictedRoute>
										}
									/>
							<Route
									path="data-collection/:id"
									element={
										<TrialRestrictedRoute>
											<CollectionRecordDetail />
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="field-capture"
									element={
										<TrialRestrictedRoute>
											<FieldDataCapture />
										</TrialRestrictedRoute>
									}
								/>
								</>
							)}
							<Route
								path="slim-form/capture"
									element={
										<TrialRestrictedRoute>
											{user?.hasPlusSubscription ||
											user?.is_trial || user?.isTrial ||
											user?.isTrialAccount ? (
												<SlimFormCapture />
											) : (
												<PlusSubscriptionRequired featureName="SlimForm" />
											)}
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="slim-form/queue"
									element={
										<TrialRestrictedRoute>
											{user?.hasPlusSubscription ||
											user?.is_trial || user?.isTrial ||
											user?.isTrialAccount ? (
												<SlimFormQueue />
											) : (
												<PlusSubscriptionRequired featureName="SlimForm" />
											)}
										</TrialRestrictedRoute>
									}
								/>
								<Route
									path="slim-form/review/:id"
									element={
										<TrialRestrictedRoute>
											{user?.hasPlusSubscription ||
											user?.is_trial || user?.isTrial ||
											user?.isTrialAccount ? (
												<SlimFormReview />
											) : (
												<PlusSubscriptionRequired featureName="SlimForm" />
											)}
										</TrialRestrictedRoute>
									}
								/>
								{/* FacilityLens */}
								<Route
									path="facility-lens"
									element={
										<TrialRestrictedRoute>
											<FacilityLens />
										</TrialRestrictedRoute>
									}
								/>
							</Routes>
						</Col>
					</div>
				</Container>
			</Row>

			{/* Sticky Footer */}
			<footer className="app-footer">
				<Copyright />
			</footer>

			{/* Expired Trial Modal */}
			<ExpiredTrialModal open={showExpiredModal} onClose={closeExpiredModal} />

			{/* Lapsed Subscription Modal */}
			<LapsedSubscriptionModal
				open={showLapsedModal}
				onClose={closeLapsedModal}
				subscriptionStatus={subscriptionStatus?.status as any}
			/>
		</div>
	);
};

export { SignedIn };
